import { Observer, Subject, Subscription, asyncScheduler, debounceTime, filter, map, mergeMap, timer } from "rxjs";
import { PromiseDefer, getReplayPage } from "./api";
import * as paho_mqtt from "paho-mqtt";
import axios from "axios";
// import { Buffer } from "buffer";
import { getAllPages } from "../views/experience/leanbot-core/replay";
import { mqttTransport as transport } from "./mqtt-transport";

export enum MQTT_CHANNEL {
    TELEMETRY = conf.mqtt.gatewaytopic,
    SIMULATION = conf.mqtt.topic,
    REPLAY = conf.mqtt.replay,
    LEANBOT_CMD = conf.mqtt.leanbot_cmd
}

type MqttChannelName = keyof typeof MQTT_CHANNEL;
type ChannelNameTypes = {
    [K in MqttChannelName]?: boolean;

};

interface ISubscriptionRecord {
    [username: string]: ChannelNameTypes;

}
export interface IAddtionOptions {
    replayId?: number | string;

}

export type GatewayMqttMessage = paho_mqtt.Message & {
    channel?: string;
    mission?: string;
};

type SemanticSubscription = 'mission' | 'sim';

/** Channel label the gateway stamps on raw-topic messages. */
const RAW_TOPIC_CHANNEL = 'rawTopic';

/**
 * MQTT topic matching with `+` (một cấp) và `#` (nhiều cấp).
 *
 * Cần ở phía client vì một lệnh subscribe theo pattern nhận về topic cụ thể:
 * đăng ký `factory/#` thì message tới mang `destinationName` là
 * `factory/gw-01/telemetry`, phải khớp lại mới biết gọi callback nào.
 */
export function topicMatches(pattern: string, topic: string): boolean {
    if (pattern === topic) return true;
    const p = pattern.split('/');
    const t = topic.split('/');
    for (let i = 0; i < p.length; i++) {
        if (p[i] === '#') return true;
        if (p[i] === '+') {
            if (t[i] === undefined) return false;
            continue;
        }
        if (p[i] !== t[i]) return false;
    }
    return p.length === t.length;
}

/** #576: text that matches itself inside a RegExp (a username may contain `$`, `.`, `(`...). */
function escapeRegExp(text: string): string {
    return String(text).replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
}


class MQTTService {
    mqttClient: paho_mqtt.Client;
    username: string;
    private topic: any;
    private urlmqtt: any;
    private portmqtt: any;
    private onMessageArrived: Subject<GatewayMqttMessage>;
    onConnected: Subject<paho_mqtt.Client>;
    onConnectLost: Subject<paho_mqtt.MQTTError>;
    onIdleTimeout: Subject<void>;
    /** Re-emits messages handed to WebSocket so embedders can surface them. */
    onGatewaySend: Subject<string>;
    onGatewayDiagnostic: Subject<string>;
    private subscriptionList: Subscription[] = [];
    private registerChannel: ISubscriptionRecord = {};
    private replayList: any = {};
    private lastReplayList: any = {};
    private flag_connected = 0;
    private semanticRefCounts: Record<SemanticSubscription, number> = { mission: 0, sim: 0 };
    enableTelemetry: boolean;
    messageDelay: any;

    enableLogRawMessage = false;
    enableLogAllMessage = false;
    enableLogRenderTimeSync = false;     // Đổi sang "true" để xem log
    //replaySpeedUp: number = 1;   
    speedUp = 1;                        // 1 SpeedUp chung cho cả Realtime và Replay
    // private registerTopic: any = {};

    constructor() {
        // #609: the broker credential (leanbot_token) is no longer fetched by the page;
        // MQTT goes through the authenticated gateway connection.
        this.topic = conf.mqtt.gatewaytopic;
        this.urlmqtt = conf.mqtt.url;
        this.portmqtt = conf.mqtt.port;
        this.onMessageArrived = new Subject<GatewayMqttMessage>();
        this.onConnected = new Subject();
        this.onConnectLost = new Subject<paho_mqtt.MQTTError>();
        this.onIdleTimeout = new Subject<void>();
        this.onGatewaySend = new Subject<string>();
        this.onGatewayDiagnostic = new Subject<string>();

        this.enableTelemetry = true;
        this.messageDelay = this.onMessageArrived;
        transport.onMessage.subscribe(message => {
            if (this.enableLogAllMessage) {
                console.log("\x1b[36mMQTT:\x1b[0m:", message.payloadString, '==', message.destinationName);
            }
            this.onMessageArrived.next(message as any);
        });
        transport.onConnected.subscribe(() => {
            this.flag_connected = 1;
            console.log("MQTT connected");
            this.subscribeRegisteredTopics();
            this.restoreSemanticSubscriptions();
            this.onConnected.next(transport as any);
        });
        transport.onConnectionLost.subscribe(errorMessage => {
            this.flag_connected = 0;
            this.onConnectLost.next({ errorMessage } as paho_mqtt.MQTTError);
        });
        transport.onIdleTimeout.subscribe(() => {
            this.flag_connected = 0;
            console.log("MQTT idle timeout: closing connection");
            this.onIdleTimeout.next();
        });
        transport.onGatewaySend.subscribe(message => this.onGatewaySend.next(message));
        transport.onDiagnostic.subscribe(message => this.onGatewayDiagnostic.next(message));
        // .pipe(
        //     mergeMap((val:Paho.MQTT.Message)=>{
        //         return timer(10).pipe(map(()=>val)) // 10ms debounce time
        //     })
        // );

    }
    disableTelemetry() {
        this.enableTelemetry = false;
    }
    reset() {
        this.enableTelemetry = true;
    }
    private restoreSemanticSubscriptions() {
        if (this.semanticRefCounts.mission > 0) {
            transport.startMission().catch(console.error);
        }
        if (this.semanticRefCounts.sim > 0) {
            transport.startSim().catch(console.error);
        }
    }
    private startSemanticSubscription(kind: SemanticSubscription) {
        if (kind === 'mission') {
            transport.startMission().catch(console.error);
        } else {
            transport.startSim().catch(console.error);
        }
    }
    private stopSemanticSubscription(kind: SemanticSubscription) {
        if (kind === 'mission') {
            transport.stopMission().catch(console.error);
        } else {
            transport.stopSim().catch(console.error);
        }
    }
    private subscribeSemantic(
        kind: SemanticSubscription,
        onMessageArrived: (message: GatewayMqttMessage) => void,
    ): Subscription {
        this.semanticRefCounts[kind] += 1;
        if (this.semanticRefCounts[kind] === 1) {
            this.startSemanticSubscription(kind);
        }

        const channel = kind === 'mission' ? 'telemetry' : 'sim';
        let lastPayload = '';
        let lastPayloadAt = 0;
        const sub = this.messageDelay.pipe(
            filter((message: GatewayMqttMessage) => message?.channel === channel),
            filter((message: GatewayMqttMessage) => {
                if (kind === 'mission') return true;
                const now = performance.now();
                if (message.payloadString === lastPayload && now - lastPayloadAt <= 1500) {
                    return false;
                }
                lastPayload = message.payloadString;
                lastPayloadAt = now;
                return true;
            }),
        ).subscribe((message: GatewayMqttMessage) => onMessageArrived(message));

        sub.add(() => {
            this.semanticRefCounts[kind] = Math.max(0, this.semanticRefCounts[kind] - 1);
            if (this.semanticRefCounts[kind] === 0) {
                this.stopSemanticSubscription(kind);
            }
        });
        this.subscriptionList.push(sub);
        return sub;
    }
    // ──────────────────────────────────────────────────────────────────────
    // Raw-topic API. Đường đi riêng, không đụng subscribeMission/subscribeSim:
    // hai hàm đó lọc theo channel 'telemetry'/'sim' nên message raw không bao
    // giờ lọt qua được.
    // ──────────────────────────────────────────────────────────────────────

    /**
     * Nghe một topic bất kỳ trên broker.
     *
     * @param topic Topic filter gửi nguyên văn cho gateway; cho phép `+` và `#`.
     * @returns Subscription — huỷ nó cũng huỷ luôn đăng ký ở gateway.
     */
    subscribeTopic(
        topic: string,
        onMessageArrived: (message: GatewayMqttMessage) => void,
    ): Subscription {
        transport.subscribeTopic(topic).catch(error => {
            // Broker có ACL riêng: một topic ngoài nhánh được phép sẽ bị từ chối
            // ở SUBACK và lỗi chỉ hiện ra ở đây chứ không ở lệnh publish.
            console.error(`subscribeTopic(${topic}) failed:`, error);
        });

        const sub = this.onMessageArrived.pipe(
            filter((message: GatewayMqttMessage) => message?.channel === RAW_TOPIC_CHANNEL),
            filter((message: GatewayMqttMessage) =>
                topicMatches(topic, message?.destinationName || ''),
            ),
        ).subscribe((message: GatewayMqttMessage) => onMessageArrived(message));

        sub.add(() => {
            transport.unsubscribeTopic(topic).catch(console.error);
        });
        this.subscriptionList.push(sub);
        return sub;
    }

    /** Gửi payload lên một topic bất kỳ. Không cho wildcard — gateway sẽ từ chối. */
    publishTopic(topic: string, payload: string): Promise<void> {
        return transport.publishTopic(topic, payload);
    }

    subscribeMission(onMessageArrived: (message: GatewayMqttMessage) => void): Subscription {
        return this.subscribeSemantic('mission', onMessageArrived);
    }
    subscribeSim(onMessageArrived: (message: GatewayMqttMessage) => void): Subscription {
        return this.subscribeSemantic('sim', onMessageArrived);
    }
    sendCmdStart(): Promise<void> {
        return transport.sendCmdStart();
    }
    beginSendReplayData(replayId: number | string) {
        console.log("CALL TRACE 2: Nút START trên Giao diện gọi hàm beginSendReplayData() -> Tiến hành resolve Promise!");
        this.replayList[replayId].resolve();
    }
    private getTopic(username: string, defaultTopic: MQTT_CHANNEL) {
        // var surfix=(defaultTopic as any).split("/").pop();
        // #576: a function inserts the username as it is (a replacement string reads `$&`, `$'`...).
        let topic = (defaultTopic as any).replace(/\/\+\//, () => "/" + username + "/");
        // debugger;
        if (defaultTopic == MQTT_CHANNEL.TELEMETRY) {
            topic += '/+';
        }

        // var topicmqtt1 = topic + '/+'
        return topic;
    }
    private subscribeRegisteredTopics() {
        Object.keys(this.registerChannel).forEach((username) => {
            const listChannel = this.registerChannel[username];
            (Object.keys(listChannel) as MqttChannelName[]).forEach((channelName) => {
                transport.subscribe(this.getTopic(username, MQTT_CHANNEL[channelName])).catch(console.error);
            })

        });
    }
    /**
     * register callback to handle message
     * @param username 
     * @param onMessageArrived 
     * @returns 
     */
    // subscribe(username: string, onMessageArrived: (message: Paho.MQTT.Message) => void):Subscription;
    subscribe(username: string, topic: MQTT_CHANNEL, onMessageArrived: (message: Paho.MQTT.Message) => void, options?: IAddtionOptions): Subscription | undefined {
        if (topic == undefined) {
            return;
        }
        // check already init subscription
        const surfix = (topic as any).split("/").pop();
        const channel = MQTT_CHANNEL[topic] as MqttChannelName;
        this.registerChannel[username] = this.registerChannel[username] || {};
        this.registerChannel[username][channel] = true;

        // #576: the username is matched as it is; `$`, `.` or `(` in it are not RegExp syntax.
        const isMineMessage = new RegExp(`^leanbot\/${escapeRegExp(username)}\/${escapeRegExp(surfix)}`, "i")
        console.log("isMineMessage:", isMineMessage, surfix);
        let lastMessage: Paho.MQTT.Message;
        let lastTime = performance.now();
        const sub = this.messageDelay.pipe(filter((msg: Paho.MQTT.Message) => {

            if (msg == undefined || msg == null) return false;
            const des = msg.destinationName || "";
            return !!des.match(isMineMessage);
        }),
            filter((msg: Paho.MQTT.Message) => {
                if (surfix == "telemetry") {
                    return true;
                }
                // remove duplicate message from simulator
                if (lastMessage && lastMessage.payloadString == msg.payloadString) {
                    if (performance.now() - lastTime > 1500) {
                        lastTime = performance.now();
                        return true;
                    }
                    console.log("remove duplicate message from simulator", msg.payloadString);
                    return false;
                }
                lastMessage = msg;

                return true;
            })

        )
            .subscribe((message: Paho.MQTT.Message) => {
                if (this.enableLogRawMessage) {
                    console.log("send sub message", message.payloadString);
                }
                if (onMessageArrived != undefined) {
                    onMessageArrived(message);
                }
            });
        this.subscriptionList.push(sub);
        if (this.flag_connected == 1) {
            transport.subscribe(this.getTopic(username, topic)).catch(console.error);
        }

        if (options && options.replayId != undefined && typeof this.replayList[options.replayId] == "undefined") {
            console.log("CALL TRACE 1: Hàm subscribe() tự động gọi tới setupReplayData() ở lần đầu!");
            this.replayList[options.replayId] = new PromiseDefer();
            this.setupReplayData({ surfix, username }, options)
        }

        return sub;
    }
    startFetchReplay(replayId: number | string) {
        // debugger;
        console.log("CALL TRACE 3: Nút REPLAY AGAIN trên Giao diện gọi startFetchReplay() -> gọi lại Cache của setupReplayData() cũ!");
        this.lastReplayList[replayId]();
    }
    public sendMessage(topic: MQTT_CHANNEL, message: Paho.MQTT.Message) {
        // message.destinationName is the username
        const destinationName = (topic as any).replace(/\/\+\//, () => "/" + message.destinationName + "/");
        const me = this;
        return new Promise((resolve) => {
            asyncScheduler.schedule(function () {
                if (me.flag_connected == 1) {
                    transport.publish(destinationName, message.payloadString).then(() => resolve(true)).catch(() => {
                        this.schedule({}, 100);
                    });
                } else {

                    this.schedule({}, 100);
                }
            }, 0);
        });

    }
    public disconnect() {
        this.flag_connected = 0;
        transport.disconnectGlobal().catch(console.error);
    }
    public reconnect() {
        console.log("MQTT reconnect requested");
        return transport.reconnect();
    }
    private async setupReplayData(inf: { surfix: string, username: string }, options: IAddtionOptions) {

        // get all data from replay http server
        // var count=0;
        // var page=1;
        const me = this;
        const desination = `leanbot/${inf.username}/${inf.surfix}`;
        me.lastReplayList[options.replayId] = () => {
            // debugger;
            let queueOrder = Promise.resolve();
            let lastMessage: any = null;
            // asyncScheduler.schedule(async function (page) {
            // debugger;


            // data.sort((a: { content: string; },b: { content: string; })=>{
            //     var ajs=JSON.parse(a.content);
            //     var bjs=JSON.parse(b.content);
            //     if (ajs.RobotStatus.match(/Ready/i)) {
            //       ajs.RobotTime=-999;
            //     } 
            //     if (bjs.RobotStatus.match(/Ready/i)) {
            //       bjs.RobotTime=-999;
            //     } 
            //     return ajs.RobotTime-bjs.RobotTime;
            // });
            // debugger;
            let msg: Paho.MQTT.Message;
            // if (data  && data.length) {
            // if (page == 1) {
            //     msg = {
            //         destinationName: desination,
            //         payloadString: data[0].content,
            //     } as any;
            //     me.onMessageArrived.next(msg);
            // }
            new Promise(async (resolve) => {
                const data = await getAllPages(options.replayId);

                queueOrder = queueOrder.then(() => data.reduce((prev: Promise<any>, curr: any, index: number) => {
                    if (index == 0) {
                        msg = {
                            destinationName: desination,
                            payloadString: curr.content,
                        } as any;

                        console.log(`Skip Promise START! Send message index=0 (RobotTime=${JSON.parse(curr.content).RobotTime})`);
                        me.onMessageArrived.next(msg);
                        return prev;
                    }
                    return prev.then(() => {

                        // max rpm is 15, diameter is 30
                        // 1 round=30mm*pi
                        // 0.25rps*30mm*pi=7.5mm per sec
                        return new Promise((resolve) => {
                            // setTimeout(() => {
                            if (index == 1) {
                                console.log(`Flag Promise START đã mở hoặc Replay Again, Bắt đầu rải message từ index=1 (RobotTime=${JSON.parse(curr.content).RobotTime})`);
                            }
                            console.log("msg sent", index);
                            me.onMessageArrived.next({

                                destinationName: desination,
                                payloadString: curr.content,

                            } as any);
                            const js = JSON.parse(curr.content);
                            const tm = parseFloat(js.RobotTime);
                            if (!isNaN(tm) && tm > 0) {
                                lastMessage = curr;
                            }
                            resolve(curr);
                            // }, 0);

                        })


                    })
                }, me.replayList[options.replayId].promise));
                resolve([]);
            })
            // debugger;
            // this.schedule(page + 1);
            // } else {
            //     queueOrder.then(()=>{

            //         if (lastMessage) {
            //             var js=JSON.parse(lastMessage.content);
            //             js.RobotStatus="Finish";
            //             me.onMessageArrived.next({

            //                 destinationName: desination,
            //                 payloadString: JSON.stringify(js),

            //             } as any);
            //         }
            //     })

            // }
            // }, 0, 1);
        }

        me.lastReplayList[options.replayId]();

        // throw new Error("Method not implemented.");
    }
    get isConnectionOnDemand() { return transport.isConnectionOnDemand; }
    get connectionAllowed() { return transport.connectionAllowed; }
    /** #576: whether the gateway socket is up (the /gateway page shows it). */
    get isConnected() { return transport.isConnected; }
    requestConnection(): Promise<void> { return transport.requestConnection(); }
    /** #576: relay one device frame (/gateway); see MqttGatewayClient.sendDeviceData. */
    sendDeviceData(data: string): boolean { return transport.sendDeviceData(data); }

    async connect() {
        if (this.flag_connected == 1) {
            return;
        }
        this.username = sessionStorage.getItem("USERNAME") || "";

        // this.registerTopic[username]=false;
        await transport.connect();
        this.mqttClient = transport as any;
        this.flag_connected = 1;
        this.subscribeRegisteredTopics();
        this.restoreSemanticSubscriptions();

        return transport as any;
    }
}
export const mqttService = new MQTTService();
